import React, { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { getOrganisationBanks } from "../../../../../api";
import config from "../../../../../config";
import CheckGreenCircleIcon from "../../../../assets/svg/icons/common/check-green-circle";
import FancyCheckbox from "../../../../components/form/fancy-checkbox";
import CaretDown24 from "../../../../assets/svg/icons/common/CaretDown24";
import useUserStore from "../../../../../v2/stores/user";
import useOrganizationStore from "../../../../stores/organization";
import { processorInfoSchema } from "../../validation-schema";
import baseStyles from "../../style.module.scss";
import styles from "./style.module.scss";
import { useEquipmentsByTypeAndBrand } from "../equipments/useEquipmentsByTypeAndBrand";
import useEquipmentQuantity from "../../../../hooks/useEquipmentQuantity";
import {
  isAchProcessorId,
  hasKurvProcessor,
  achPricingPatchForProcessorChange,
  ccPricingPatchForProcessorChange,
  withKurvTierRules,
} from "../../utils/processor-pricing-rules";

const Processors = ({ isOpen, onToggle }) => {
  const { values, setFieldValue, setValues, touched, errors } =
    useFormikContext();
  const [processorList, setProcessorList] = useState([]);
  const [processorListLoaded, setProcessorListLoaded] = useState(false);
  const organizationId = useUserStore((state) => state.organizationId);
  const pricingDefaults = useOrganizationStore(
    (state) => state.pricingDefaults,
  );
  const hasErrors = !processorInfoSchema.isValidSync(values);
  const { handleAddEquipment } = useEquipmentQuantity();
  const { equipments } = useEquipmentsByTypeAndBrand();
  const [isKurvAutoAdded, setIsKurvAutoAdded] = useState(false);

  const handleProcessorsChange = (event, id) => {
    const { checked } = event.target;
    const sameBucket = (p) => isAchProcessorId(p) === isAchProcessorId(id);
    const processors = checked
      ? [...values.processors.filter((p) => !sameBucket(p)), id]
      : values.processors.filter((p) => p !== id);

    const patch = {
      ...achPricingPatchForProcessorChange({
        values,
        nextProcessors: processors,
        pricingDefaults,
      }),
      ...ccPricingPatchForProcessorChange({
        values,
        nextProcessors: processors,
        pricingDefaults,
      }),
    };

    setValues((prev) => ({
      ...prev,
      processors,
      ...patch,
      ...withKurvTierRules({
        values: prev,
        nextProcessors: processors,
        pricingDefaults,
        patch,
      }),
    }));
  };

  useEffect(() => {
    const fetchProcessors = async () => {
      const response = await getOrganisationBanks();
      if (response.status) {
        setProcessorList(response.data);
        setProcessorListLoaded(true);
      }
    };
    fetchProcessors();
  }, []);

  useEffect(() => {
    if (!processorListLoaded) {
      return;
    }

    const availableProcessorIds = new Set(
      processorList.map((processor) => String(processor.value)),
    );
    const selectedProcessors = Array.isArray(values.processors)
      ? values.processors
      : [];
    const availableSelections = selectedProcessors.filter((processorId) =>
      availableProcessorIds.has(String(processorId)),
    );

    if (availableSelections.length !== selectedProcessors.length) {
      setValues((previousValues) => ({
        ...previousValues,
        processors: availableSelections,
        processorSelectionLocked: false,
      }));
    }
  }, [processorList, processorListLoaded, setValues, values.processors]);

  // Select Kurv Merchant for Other Org If processor Kurv is selected
  useEffect(() => {
    const kurvMerchant = values?.equipments.find(
      (item) => item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
    );

    if (
      hasKurvProcessor(values?.processors) &&
      organizationId !== config.paymentcloud.KURVPAY_ORG_ID &&
      !kurvMerchant
    ) {
      const firstMatchEquipment = equipments.find(
        (item) => item.id === config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
      );

      if (firstMatchEquipment && firstMatchEquipment?.id) {
        firstMatchEquipment.type = "monthly_fee";
        firstMatchEquipment.quantity = 1;
        handleAddEquipment(firstMatchEquipment);
        setIsKurvAutoAdded(true);
      }
    }

    if (!hasKurvProcessor(values?.processors) && isKurvAutoAdded) {
      const equipmentWithoutKurvMerchant = values?.equipments.filter(
        (e) => e.id !== config.paymentcloud.KURV_MERCHANT_EQUIPMENT_ID,
      );
      setFieldValue("equipments", equipmentWithoutKurvMerchant);
      setIsKurvAutoAdded(false);
    }
  }, [values?.processors]);

  return (
    <div className={baseStyles.section}>
      <div className={baseStyles.sectionHeader} onClick={onToggle}>
        <div
          className={`${baseStyles.sectionHeaderLeft} ${isOpen && baseStyles.open}`}
        >
          <CaretDown24 />
          <h6 className={baseStyles.sectionTitle}>Processor Selection</h6>
        </div>
        <div className={baseStyles.sectionHeaderRight}>
          {!hasErrors && <CheckGreenCircleIcon />}
        </div>
      </div>

      {isOpen && (
        <div className={styles.content}>
          <div className={styles.processorContainer}>
            {processorList.length === 0 ? (
              <p className={styles.processorSubHeading}>
                No processors found. Contact your administrator.
              </p>
            ) : (
              processorList.map((item, index) => (
                <FancyCheckbox
                  key={index}
                  ellipsis
                  title={item.label}
                  label={item.label}
                  width={"100%"}
                  icon={
                    config.paymentcloud.STRATUS_URL + "bank-image/" + item.value
                  }
                  id={item.value}
                  name={"processors"}
                  checked={(values?.processors ?? []).some(
                    (processorId) =>
                      String(processorId) === String(item.value),
                  )}
                  disabled={values.processorSelectionLocked}
                  onChange={(event) =>
                    handleProcessorsChange(event, item.value)
                  }
                />
              ))
            )}
          </div>
          {hasErrors && touched.processors ? (
            <p className={styles.error}>{errors.processors}</p>
          ) : (
            ""
          )}
        </div>
      )}
    </div>
  );
};

export default Processors;
